"use client";

import Link from "next/link";
import { useRef } from "react";
import gsap from "gsap";

interface CTAButtonProps {
  href: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  children: string;
}

export default function CTAButton({ href, onClick, children }: CTAButtonProps) {
  const topLettersRef = useRef<(HTMLSpanElement | null)[]>([]);
  const bottomLettersRef = useRef<(HTMLSpanElement | null)[]>([]);

  const letters = children.split("");

  const handleMouseEnter = () => {
    gsap.killTweensOf([topLettersRef.current, bottomLettersRef.current]);
    const tl = gsap.timeline();

    tl.to(
      topLettersRef.current,
      {
        yPercent: -100,
        duration: 0.35,
        ease: "power2.inOut",
        stagger: 0.015, // Slightly faster stagger for a tighter feel
      },
      0,
    );

    tl.to(
      bottomLettersRef.current,
      {
        yPercent: -100,
        duration: 0.35,
        ease: "power2.inOut",
        stagger: 0.015,
      },
      0,
    );
  };

  const handleMouseLeave = () => {
    gsap.killTweensOf([topLettersRef.current, bottomLettersRef.current]);
    const tl = gsap.timeline();

    tl.to(
      topLettersRef.current,
      {
        yPercent: 0,
        duration: 0.35,
        ease: "power2.inOut",
        stagger: 0.01,
      },
      0,
    );

    tl.to(
      bottomLettersRef.current,
      {
        yPercent: 0,
        duration: 0.35,
        ease: "power2.inOut",
        stagger: 0.01,
      },
      0,
    );
  };

  return (
    <Link
      href={href}
      prefetch={false}
      className="cta-button group inline-flex items-center justify-center overflow-hidden"
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ padding: "8px 20px" }}
    >
      {/* 1. h-[1em] makes the mask exactly the height of the font.
          2. leading-none removes default line-height spacing.
      */}
      <div className="relative overflow-hidden h-[1em] leading-none flex items-center">
        {/* Layer 1 */}
        <div className="flex">
          {letters.map((char, i) => (
            <span
              key={`top-${i}`}
              ref={(el) => { topLettersRef.current[i] = el; }}
              className="inline-block"
              style={{ whiteSpace: char === " " ? "pre" : "normal" }}
            >
              {char}
            </span>
          ))}
        </div>

        {/* Layer 2 - Positioned exactly at the bottom of the mask */}
        <div className="flex absolute top-full left-0">
          {letters.map((char, i) => (
            <span
              key={`bottom-${i}`}
              ref={(el) => { bottomLettersRef.current[i] = el; }}
              className="inline-block"
              style={{ whiteSpace: char === " " ? "pre" : "normal" }}
            >
              {char}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}
